/**
 * The last resort. A render error inside a panel must not take the countdown
 * with it — but React only offers class components for this, so this is the one
 * class in the codebase.
 *
 * It renders `last known + a reason`, never a blank. That is the same rule
 * components.md §4.1 applies to a dropped socket, for the same reason: a panel
 * that blanks looks like the product has stopped, and this product is judged on
 * whether it looks like it has stopped.
 */

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { currentReporter } from './error-reporting'

interface Props {
  children: ReactNode
  /** Names the panel in the fallback, so the user can say which one broke. */
  label?: string
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /*
      **Reported, and counted.** Phase 10.

      Until then this logged and moved on, which means a panel that threw on
      every `book` frame produced twenty console lines a second and no number
      anybody could quote. `ErrorReporter` folds the repeats into one record with
      a count, redacts the message on the way in, and calls its sink — which is
      still `console.error`, so nothing that reads the console has lost anything.

      `currentReporter()` rather than a prop or a context: this component's whole
      job is to work when the tree below it does not, and reading a context in
      `componentDidCatch` is one more thing that can be missing at exactly the
      wrong moment. If the reporter never installed, the fallback below still
      renders — the boundary is not allowed to depend on it.
    */
    const label = this.props.label ?? 'app'
    const reporter = currentReporter()
    if (reporter) reporter.report('render', error, label)
    else console.error(`[uptik] render error in ${label}`, error, info.componentStack)
  }

  override render(): ReactNode {
    if (!this.state.error) return this.props.children
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-1 bg-bg-surface p-4 text-center">
        <span className="text-body-sm text-down-text">
          {this.props.label ? `${this.props.label} stopped rendering` : 'this panel stopped rendering'}
        </span>
        <span className="text-caption text-text-tertiary">reload to restore it</span>
      </div>
    )
  }
}
